import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type UserType = 'INDIVIDUAL' | 'CORPORATE';
export type Gender = 'M' | 'F' | '';

export type FormData = {
    nik: string;
    fullName: string;
    birthPlace: string;
    birthDate: string; // YYYY-MM-DD
    gender: Gender;
    email: string;
    password: string;
    passwordConfirm: string;
    address: string;
    city: string;
    province: string;
    npwp: string;
    // Setelah direct-upload ke MinIO, yang kita simpan cuma URL-nya.
    // Preview URL (data URL) opsional untuk tampilan thumbnail.
    ktpPhotoUrl: string | null;
    ktpPhotoPreview: string | null;
    npwpPhotoUrl: string | null;
    npwpPhotoPreview: string | null;
};

export type CertStatusCode =
    | 'INITIATED'
    | 'REGISTERED'
    | 'EKYC_PENDING'
    | 'CERT_ACTIVE'
    | 'CERT_REJECTED'
    | 'CERT_EXPIRED';

export type VerifStatus = 'ok' | 'pending' | 'error';

export type VerificationSummary = {
    phone: VerifStatus;
    registration: VerifStatus;
    liveness: VerifStatus;
    signature: VerifStatus;
};

type State = {
    lang: 'id' | 'en';
    sessionId: string | null;
    step: number;
    userType: UserType | null;
    phone: string;
    phoneCountryCode: string;
    phoneVerified: boolean;
    formData: FormData;
    signatureDataUrl: string | null;
    livenessBlob: Blob | null;
    registrationId: number | null;
    certStatus: CertStatusCode | null;
    verification: VerificationSummary;
    lastError: string | null;
};

type Actions = {
    setLang: (lang: 'id' | 'en') => void;
    setSession: (id: string, step: number) => void;
    setStep: (step: number) => void;
    setUserType: (t: UserType) => void;
    setPhone: (phone: string) => void;
    setPhoneVerified: (v: boolean) => void;
    updateFormData: <K extends keyof FormData>(key: K, value: FormData[K]) => void;
    setSignatureDataUrl: (url: string | null) => void;
    setLivenessBlob: (blob: Blob | null) => void;
    setRegistrationId: (id: number | null) => void;
    setCertStatus: (s: CertStatusCode | null) => void;
    setVerification: (v: Partial<VerificationSummary>) => void;
    setError: (msg: string | null) => void;
    reset: () => void;
};

const EMPTY_FORM: FormData = {
    nik: '',
    fullName: '',
    birthPlace: '',
    birthDate: '',
    gender: '',
    email: '',
    password: '',
    passwordConfirm: '',
    address: '',
    city: '',
    province: '',
    npwp: '',
    ktpPhotoUrl: null,
    ktpPhotoPreview: null,
    npwpPhotoUrl: null,
    npwpPhotoPreview: null,
};

const INIT_STATE: State = {
    lang: 'id',
    sessionId: null,
    step: 1,
    userType: null,
    phone: '',
    phoneCountryCode: '+62',
    phoneVerified: false,
    formData: EMPTY_FORM,
    signatureDataUrl: null,
    livenessBlob: null,
    registrationId: null,
    certStatus: null,
    verification: {
        phone: 'pending',
        registration: 'pending',
        liveness: 'pending',
        signature: 'pending',
    },
    lastError: null,
};

/**
 * Store utama onboarding.
 * Sebagian state (FormData File, Blob) tidak masuk persist — tidak bisa di-serialize.
 */
export const useOnboardingStore = create<State & Actions>()(
    persist(
        (set) => ({
            ...INIT_STATE,

            setLang: (lang) => set({ lang }),
            setSession: (sessionId, step) => set({ sessionId, step }),
            setStep: (step) => set({ step }),
            setUserType: (userType) => set({ userType }),
            setPhone: (phone) => set({ phone }),
            setPhoneVerified: (phoneVerified) => set({ phoneVerified }),

            updateFormData: (key, value) =>
                set((state) => ({ formData: { ...state.formData, [key]: value } })),

            setSignatureDataUrl: (signatureDataUrl) => set({ signatureDataUrl }),
            setLivenessBlob: (livenessBlob) => set({ livenessBlob }),
            setRegistrationId: (registrationId) => set({ registrationId }),
            setCertStatus: (certStatus) => set({ certStatus }),

            setVerification: (patch) =>
                set((state) => ({ verification: { ...state.verification, ...patch } })),
            setError: (lastError) => set({ lastError }),

            reset: () => set(INIT_STATE),
        }),
        {
            name: 'peruri-onboarding',
            // File & Blob tidak bisa di-persist. Juga skip error.
            partialize: (state) => ({
                lang: state.lang,
                sessionId: state.sessionId,
                step: state.step,
                userType: state.userType,
                phone: state.phone,
                phoneCountryCode: state.phoneCountryCode,
                phoneVerified: state.phoneVerified,
                formData: {
                    ...state.formData,
                    // Jangan persist preview data URL (bisa ratusan KB di localStorage)
                    ktpPhotoPreview: null,
                    npwpPhotoPreview: null,
                    password: '', // jangan persist password plain
                    passwordConfirm: '',
                },
                signatureDataUrl: state.signatureDataUrl,
                registrationId: state.registrationId,
                certStatus: state.certStatus,
                verification: state.verification,
            }),
        }
    )
);
